import {
  createResource,
  createSignal,
  Show,
  type Component,
} from "solid-js";
import { useParams, A } from "@solidjs/router";
import { Heart, Pencil } from "lucide-solid";
import { api, fetchBlob, type Snip } from "../lib/api";
import { currentUser, isLoggedIn } from "../lib/auth";
import Markdown from "../components/Markdown";
import Spinner from "../components/Spinner";

type SnipWithContent = { snip: Snip; content: string };

async function fetchSnip(id: number): Promise<SnipWithContent> {
  const { data, error } = await api.GET("/snips/{id}", {
    params: { path: { id } },
  });
  if (error || !data) throw new Error("Gönderi bulunamadı");
  const firstBlob = data.files?.[0]?.blobId;
  const content = firstBlob ? await fetchBlob(firstBlob) : "";
  return { snip: data, content };
}

const SnipView: Component = () => {
  const params = useParams();
  const [data, { mutate }] = createResource(
    () => Number(params.id),
    fetchSnip,
  );

  const [pending, setPending] = createSignal(false);

  const toggleLike = async () => {
    const current = data();
    if (!current || pending()) return;
    setPending(true);
    const liked = current.snip.liked ?? false;
    try {
      if (liked) {
        await api.DELETE("/snips/{id}/like", {
          params: { path: { id: current.snip.id! } },
        });
      } else {
        await api.POST("/snips/{id}/like", {
          params: { path: { id: current.snip.id! } },
        });
      }
      // Optimistically update the in-memory resource value.
      mutate({
        ...current,
        snip: {
          ...current.snip,
          liked: !liked,
          likeCount: (current.snip.likeCount ?? 0) + (liked ? -1 : 1),
        },
      });
    } finally {
      setPending(false);
    }
  };

  const isAuthor = () =>
    data()?.snip.author?.username === currentUser()?.username;

  return (
    <div class="mx-auto max-w-3xl px-4 py-6">
      <Show
        when={!data.loading && data()}
        fallback={
          <div class="flex justify-center py-20">
            <Spinner size={28} />
          </div>
        }
      >
        {(d) => (
          <article>
            <header class="mb-6 border-b border-border pb-4">
              <h1 class="m-0 text-2xl font-bold">{d().snip.title}</h1>
              <Show when={d().snip.summary}>
                <p class="mb-3 mt-1 text-muted-foreground">{d().snip.summary}</p>
              </Show>
              <div class="flex items-center justify-between text-sm text-muted-foreground">
                <span>@{d().snip.author?.username}</span>
                <div class="flex items-center gap-2">
                  <Show when={isLoggedIn()}>
                    <button
                      type="button"
                      onClick={toggleLike}
                      disabled={pending()}
                      class={`inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 transition-colors hover:bg-muted ${d().snip.liked ? "text-primary" : "text-foreground"
                        }`}
                    >
                      <Heart size={16} fill={d().snip.liked ? "currentColor" : "none"} />
                      {d().snip.likeCount ?? 0}
                    </button>
                  </Show>
                  <Show when={isAuthor()}>
                    <A
                      href={`/snip/${d().snip.id}/edit`}
                      class="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-foreground no-underline transition-colors hover:bg-muted"
                    >
                      <Pencil size={16} />
                      Düzenle
                    </A>
                  </Show>
                </div>
              </div>
            </header>

            <Markdown source={d().content} />
          </article>
        )}
      </Show>
    </div>
  );
};

export default SnipView;
